import React, { useState } from "react";
import { Phone, Mail, MapPin, MessageCircle, Clock, CheckCircle, AlertCircle } from "lucide-react";
import { CONTACT_PHONE, CONTACT_EMAIL, COMPANY_ADDRESS, CONTACT_WHATSAPP, LEAD_CHANNELS } from "../constants/data";
import { saveLeadToAPI } from "../services/api";

const GOLD = "#50C878";

export default function ContactPage() {
  const [form, setForm] = useState({ firstName: "", lastName: "", phone: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  function validate() {
    const e = {};
    if (!form.firstName.trim()) e.firstName = "Required";
    if (!form.phone.trim()) e.phone = "Required";
    else if (!/^[6-9]\d{9}$/.test(form.phone.replace(/\s/g, ""))) e.phone = "Enter valid 10-digit number";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter valid email";
    return e;
  }

  async function handleSubmit() {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    await saveLeadToAPI({
      first_name: form.firstName,
      last_name: form.lastName,
      phone: form.phone,
      email: form.email,
      message: form.message,
      channel: LEAD_CHANNELS.WEB,
    });
    setSubmitted(true);
  }

  const inputStyle = (field) => ({
    width: "100%",
    padding: "12px 16px",
    background: "rgba(80,200,120,0.10)",
    border: `1.5px solid ${errors[field] ? "#e05a5a" : "rgba(80,200,120,0.25)"}`,
    borderRadius: "8px",
    color: "#013220",
    fontFamily: "'Inter', sans-serif",
    fontSize: "0.9rem",
    outline: "none",
    boxSizing: "border-box",
  });

  return (
    <div style={{ background: "#F2F8F3", color: "#013220", minHeight: "100vh", paddingTop: "68px" }}>
      {/* Header */}
      <div style={{ background: "linear-gradient(135deg, #FFFFFF 0%, #F7FBF8 100%)", borderBottom: "1px solid rgba(80,200,120,0.15)", padding: "56px 24px 48px" }}>
        <div style={{ maxWidth: "720px", margin: "0 auto", textAlign: "center" }}>
          <span style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.72rem", color: GOLD, letterSpacing: "0.18em", textTransform: "uppercase" }}>
            Reach Out
          </span>
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2rem, 4vw, 3.2rem)", fontWeight: 600, color: "#013220", margin: "12px 0 16px" }}>
            Let's Talk About Your Dream Home
          </h1>
          <p style={{ color: "rgba(1,50,32,0.62)", fontFamily: "'Inter', sans-serif", fontSize: "1rem", lineHeight: 1.7 }}>
            Our associates are available Mon–Sat, 9am–7pm. We respond to all inquiries within 24 hours.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: "1280px", margin: "0 auto", padding: "60px 24px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.2fr", gap: "48px" }} className="contact-grid">
          {/* ── LEFT: Contact Info ── */}
          <div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.7rem", fontWeight: 600, color: "#013220", marginBottom: "32px" }}>
              Contact Channels
            </h2>

            {/* Channel cards */}
            {[
              {
                icon: Phone,
                label: "Call Us",
                value: `+91 ${CONTACT_PHONE}`,
                sub: "Mon–Sat, 9am–7pm",
                href: `tel:+91${CONTACT_PHONE}`,
                bg: "rgba(80,200,120,0.08)",
              },
              {
                icon: () => (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill={GOLD}>
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                ),
                label: "WhatsApp",
                value: `+91 ${CONTACT_PHONE}`,
                sub: "Chat instantly with an associate",
                href: CONTACT_WHATSAPP,
                bg: "rgba(37,211,102,0.08)",
              },
              {
                icon: Mail,
                label: "Email",
                value: CONTACT_EMAIL,
                sub: "We respond within 24 hours",
                href: `mailto:${CONTACT_EMAIL}`,
                bg: "rgba(96,165,250,0.08)",
              },
              {
                icon: MapPin,
                label: "Visit Us",
                value: "Banjara Hills, Hyderabad",
                sub: COMPANY_ADDRESS,
                href: null,
                bg: "rgba(80,200,120,0.06)",
              },
            ].map(({ icon: Icon, label, value, sub, href, bg }) => (
              <div
                key={label}
                style={{
                  display: "flex",
                  gap: "16px",
                  padding: "18px",
                  background: bg,
                  border: "1px solid rgba(80,200,120,0.15)",
                  borderRadius: "10px",
                  marginBottom: "12px",
                  textDecoration: "none",
                  color: "inherit",
                  transition: "border-color 0.2s",
                }}
                as={href ? "a" : "div"}
                onMouseOver={(e) => (e.currentTarget.style.borderColor = "rgba(80,200,120,0.4)")}
                onMouseOut={(e) => (e.currentTarget.style.borderColor = "rgba(80,200,120,0.15)")}
                onClick={() => href && window.open(href, href.startsWith("http") ? "_blank" : "_self")}
              >
                <div style={{ width: "40px", height: "40px", borderRadius: "8px", background: "rgba(80,200,120,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon size={18} color={GOLD} />
                </div>
                <div>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.72rem", color: "rgba(1,50,32,0.62)", textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 2px" }}>
                    {label}
                  </p>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.95rem", color: "#013220", fontWeight: 600, margin: "0 0 2px", cursor: href ? "pointer" : "default" }}>
                    {value}
                  </p>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.8rem", color: "rgba(1,50,32,0.62)", margin: 0 }}>
                    {sub}
                  </p>
                </div>
              </div>
            ))}

            {/* Business hours */}
            <div style={{ padding: "18px", background: "rgba(80,200,120,0.05)", border: "1px solid rgba(80,200,120,0.10)", borderRadius: "10px", marginTop: "8px" }}>
              <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                <Clock size={16} color={GOLD} style={{ marginTop: "2px", flexShrink: 0 }} />
                <div>
                  <p style={{ color: "#013220", fontFamily: "'Inter', sans-serif", fontSize: "0.88rem", fontWeight: 600, margin: "0 0 4px" }}>Business Hours</p>
                  <p style={{ color: "rgba(1,50,32,0.62)", fontFamily: "'Inter', sans-serif", fontSize: "0.82rem", lineHeight: 1.6, margin: 0 }}>
                    Monday – Saturday: 9:00 AM – 7:00 PM<br />
                    Sunday: 10:00 AM – 4:00 PM (By Appointment)
                  </p>
                </div>
              </div>
            </div>

            {/* Map */}
            {/* Map - Using the reliable embedded URL */}
<div style={{ marginTop: "24px", borderRadius: "10px", overflow: "hidden", border: "1px solid rgba(80,200,120,0.2)" }}>
  <iframe 
    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3807.033843761205!2d78.4416328!3d17.4101634!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb973b31b6bbb3%3A0x487ef31b29a13bca!2sSPRING%20ROCK%20APARTMENT!5e0!3m2!1sen!2sin!4v1781987736115!5m2!1sen!2sin"
    width="100%"
    height="220"
    style={{ border: 0, display: "block" }}
    allowFullScreen
    loading="lazy"
    referrerPolicy="no-referrer-when-downgrade"
    title="GroupBuy Flats Office Location"
  />
</div>
          </div>

          {/* ── RIGHT: Contact Form ── */}
          <div>
            <div style={{ background: "linear-gradient(135deg, #FFFFFF 0%, #F7FBF8 100%)", border: "1px solid rgba(80,200,120,0.25)", borderRadius: "16px", padding: "36px" }}>
              {submitted ? (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                  <CheckCircle size={56} color={GOLD} style={{ marginBottom: "16px" }} />
                  <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "2rem", color: "#013220", marginBottom: "12px" }}>Message Received!</h3>
                  <p style={{ color: "rgba(1,50,32,0.85)", lineHeight: 1.7, fontFamily: "'Inter', sans-serif" }}>
                    Our associate will reach you on <strong style={{ color: GOLD }}>{form.phone}</strong> within 24 hours.<br />
                    Thank you for choosing GroupBuy Flats.
                  </p>
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: "28px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
                      <div style={{ width: "32px", height: "2px", background: GOLD }} />
                      <span style={{ color: GOLD, fontFamily: "'Inter', sans-serif", fontSize: "0.72rem", letterSpacing: "0.15em", textTransform: "uppercase" }}>
                        Send a Message
                      </span>
                    </div>
                    <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.8rem", fontWeight: 600, color: "#013220", margin: 0 }}>
                      Talk To An Expertise
                    </h2>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                      <div>
                        <label style={{ display: "block", color: "rgba(1,50,32,0.85)", fontSize: "0.8rem", marginBottom: "6px", fontFamily: "'Inter', sans-serif" }}>
                          First Name <span style={{ color: GOLD }}>*</span>
                        </label>
                        <input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} style={inputStyle("firstName")}
                          onFocus={(e) => (e.target.style.borderColor = GOLD)} onBlur={(e) => (e.target.style.borderColor = errors.firstName ? "#e05a5a" : "rgba(80,200,120,0.25)")} />
                        {errors.firstName && <p style={{ color: "#e05a5a", fontSize: "0.75rem", marginTop: "4px", display: "flex", alignItems: "center", gap: "4px", fontFamily: "'Inter', sans-serif" }}><AlertCircle size={12} />{errors.firstName}</p>}
                      </div>
                      <div>
                        <label style={{ display: "block", color: "rgba(1,50,32,0.85)", fontSize: "0.8rem", marginBottom: "6px", fontFamily: "'Inter', sans-serif" }}>
                          Last Name
                        </label>
                        <input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })}  style={inputStyle("lastName")}
                          onFocus={(e) => (e.target.style.borderColor = GOLD)} onBlur={(e) => (e.target.style.borderColor = "rgba(80,200,120,0.25)")} />
                      </div>
                    </div>

                    <div>
                      <label style={{ display: "block", color: "rgba(1,50,32,0.85)", fontSize: "0.8rem", marginBottom: "6px", fontFamily: "'Inter', sans-serif" }}>
                        Phone Number <span style={{ color: GOLD }}>*</span>
                      </label>
                      <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} style={inputStyle("phone")}
                        onFocus={(e) => (e.target.style.borderColor = GOLD)} onBlur={(e) => (e.target.style.borderColor = errors.phone ? "#e05a5a" : "rgba(80,200,120,0.25)")} />
                      {errors.phone && <p style={{ color: "#e05a5a", fontSize: "0.75rem", marginTop: "4px", display: "flex", alignItems: "center", gap: "4px", fontFamily: "'Inter', sans-serif" }}><AlertCircle size={12} />{errors.phone}</p>}
                    </div>

                    <div>
                      <label style={{ display: "block", color: "rgba(1,50,32,0.85)", fontSize: "0.8rem", marginBottom: "6px", fontFamily: "'Inter', sans-serif" }}>
                        Email Address
                      </label>
                      <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} style={inputStyle("email")}
                        onFocus={(e) => (e.target.style.borderColor = GOLD)} onBlur={(e) => (e.target.style.borderColor = errors.email ? "#e05a5a" : "rgba(80,200,120,0.25)")} />
                    </div>

                    <div>
                      <label style={{ display: "block", color: "rgba(1,50,32,0.85)", fontSize: "0.8rem", marginBottom: "6px", fontFamily: "'Inter', sans-serif" }}>
                        Message / Requirements
                      </label>
                      <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} style={{ ...inputStyle("message"), resize: "vertical", lineHeight: 1.6 }}
                        onFocus={(e) => (e.target.style.borderColor = GOLD)} onBlur={(e) => (e.target.style.borderColor = "rgba(80,200,120,0.25)")} />
                    </div>

                    <button
                      onClick={handleSubmit}
                      style={{ width: "100%", padding: "15px", background: "linear-gradient(135deg, #50C878, #7FD9A0)", border: "none", borderRadius: "8px", color: "#FFFFFF", fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "1rem", cursor: "pointer", letterSpacing: "0.04em", transition: "opacity 0.2s, transform 0.2s" }}
                      onMouseOver={(e) => { e.currentTarget.style.opacity = "0.9"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                      onMouseOut={(e) => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "translateY(0)"; }}
                    >
                      Send Message — Connect Me with an Associate
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .contact-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
